import React from 'react'
import { useParams } from '@fs/zion-router'
import { usePageView } from '@fs/zion-analytics'
import { useTranslation } from 'react-i18next'
import { ResponsiveTableRow } from '@fs/zion-ui'
import { css } from '@emotion/core'
import { DmcResponsiveTable } from '../../common/DmcResponsiveTable'
import { usePageTitle } from '../../app/PageTitleContext'
import useTreeDetailChanges from './useTreeDetailChanges'

const highlightCss = css`
  background-color: yellow;
`

const getTreeInformation = (tree, t) => {
  if (!tree) return {}
  return {
    treeId: { label: t('tree.treeId', 'Tree Id'), detail: tree.treeId },
    name: { label: t('tree.name', 'Name'), detail: tree.name },
    description: { label: t('tree.description', 'Description'), detail: tree.description },
    hidden: { label: t('tree.hidden', 'Hidden'), detail: tree.hidden ? t('yes', 'Yes') : t('no', 'No') },
    private: { label: t('tree.private', 'Private'), detail: tree.private ? t('yes', 'Yes') : t('no', 'No') },
    tombstoned: {
      label: t('tree.tombstoned', 'Tombstoned'),
      detail: tree.tombstoned ? t('yes', 'Yes') : t('no', 'No'),
    },
    visibilityLocked: {
      label: t('tree.visibilityLocked', 'Visibility Locked'),
      detail: tree.visibilityLocked ? t('yes', 'Yes') : t('no', 'No'),
    },
    ordinancesAllowed: {
      label: t('tree.ordinancesAllowed', 'Ordinances Allowed'),
      detail: tree.ordinancesAllowed ? t('yes', 'Yes') : t('no', 'No'),
    },
    startingPersonId: { label: t('tree.startingPersonId', 'Starting Person Id'), detail: tree.startingPersonId },
    researcherTree: {
      label: t('tree.researcherTree', 'Researcher Tree'),
      detail: tree.researcherTree ? t('yes', 'Yes') : t('no', 'No'),
    },
    useSecondaryAdminQueues: {
      label: t('tree.useSecondaryAdminQueues', 'Use Secondary Admin Queues'),
      detail: tree.useSecondaryAdminQueues ? t('yes', 'Yes') : t('no', 'No'),
    },
    collectionId: {
      label: t('tree.collectionId', 'Collection Id'),
      detail: tree.collectionId,
    },
    autoPublicAccepted: {
      label: t('tree.autoPublicAccepted', 'Auto Public Accepted'),
      detail: tree.autoPublicAccepted ? t('yes', 'Yes') : t('no', 'No'),
    },
    devkeys: {
      label: t('tree.agentApps', 'Agent Apps'),
      detail: tree.devkeys,
    },
    ownerAccess: { label: t('tree.ownerAccess', 'Owner Access'), detail: tree.ownerAccess },
    groupAccess: { label: t('tree.groupAccess', 'Group Access'), detail: tree.groupAccess },
  }
}

const hasTreeChanges = (treeInfo1, treeInfo2) => {
  const keys1 = Object.keys(treeInfo1)
  const keys2 = Object.keys(treeInfo2)

  if (keys1.length === 0 || keys2.length === 0) {
    return keys1.length !== keys2.length
  }

  return keys1.some((key) => treeInfo1[key]?.detail?.toString() !== treeInfo2[key]?.detail?.toString())
}

const displayTreeChange = ({ primaryInfo, compareInfo }) => {
  const hasCompareData = Object.keys(compareInfo).length > 0
  return (
    <>
      {Object.entries(primaryInfo).map(([key, info]) => {
        const isDiff = hasCompareData && info.detail?.toString() !== compareInfo[key]?.detail?.toString()
        return info ? (
          <div key={key}>
            <strong>{info.label}</strong>: {isDiff ? <span css={highlightCss}>{info.detail}</span> : info.detail}
            <br />
          </div>
        ) : null
      })}
    </>
  )
}

export default function TreeDetailChanges() {
  usePageView({ page_detail: 'DMConsole:TreeChanges' })
  const { t } = useTranslation()
  usePageTitle(t('pageTitle.tree.changes'))
  const { treeId } = useParams()
  const { treeDetailChanges } = useTreeDetailChanges({ treeId })

  if (!treeDetailChanges || treeDetailChanges.length === 0) return <>{t('noChanges', 'No changes available')}</>

  return (
    <DmcResponsiveTable
      id="treeChangesTable"
      gutters="nano"
      totalColumnCount={3}
      atSizeColumns={{
        default: {
          labels: [
            t('metadata', 'Metadata'),
            t('preChangeTree', 'Pre-Change Tree'),
            t('postChangeTree', 'Post-Change Tree'),
          ],
          columnWidths: [4, 4, 4],
          combinationRules: [['column1'], ['column2'], ['column3']],
        },
      }}
    >
      {treeDetailChanges.changes.map((change) => {
        const preChangeTree = getTreeInformation(change.preChangeTree, t)
        const postChangeTree = getTreeInformation(change.postChangeTree, t)
        const noChangesDetected = !hasTreeChanges(preChangeTree, postChangeTree)
        return (
          <ResponsiveTableRow
            key={change.changeDate}
            column1={
              <>
                <strong>{t('changeType', 'Change Type')}:</strong> {change.changeType}
                <br />
                <strong>{t('changeAction', 'Action')}:</strong> {change.changeAction}
                <br />
                <strong>{t('changeDate', 'Date')}:</strong> {new Date(change.changeDate).toLocaleString()}
                <br />
                <strong>{t('contributorName', 'Contributor Name')}:</strong> {change.contributorName}
                <br />
                <strong>{t('contributorCisId', 'Contributor ID')}:</strong> {change.contributorCisId}
                <br />
                {change.submitterName && (
                  <>
                    <strong>{t('submitterName', 'Submitter Name')}:</strong> {change.submitterName}
                    <br />
                  </>
                )}
                {change.submitterCisId && (
                  <>
                    <strong>{t('submitterCisId', 'Submitter ID')}:</strong> {change.submitterCisId}
                  </>
                )}
                {
                  // Temp note while TF investigates no changes issue
                  noChangesDetected && (
                    <>
                      <br />
                      <em css={highlightCss}>
                        {t('noChangeDetected', 'Note: This was received from the tree but no changes were detected.')}
                      </em>
                    </>
                  )
                }
              </>
            }
            column2={displayTreeChange({ primaryInfo: preChangeTree, compareInfo: postChangeTree })}
            column3={displayTreeChange({ primaryInfo: postChangeTree, compareInfo: preChangeTree })}
          />
        )
      })}
    </DmcResponsiveTable>
  )
}
